import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import maplibregl from "maplibre-gl";
import RestaurantSectionCard from "./RestaurantSectionCard";
import { CHART_COLOR_PALETTE } from "src/components/charts/chartPalette";
import { BRANCHES } from "../data/restaurantDemoData";
import { useRestaurantFormatters } from "../utils/restaurantFormatters";
import type { RestaurantMetrics } from "../utils/restaurantMetrics";

interface RestaurantBranchMapProps {
  metrics: RestaurantMetrics;
}

interface ActiveBranch {
  id: string;
  name: string;
  city: string;
  revenue: number;
  orders: number;
  avgPrepMinutes: number;
  x: number;
  y: number;
}

const RestaurantBranchMap: FC<RestaurantBranchMapProps> = ({ metrics }) => {
  const { t, i18n } = useTranslation();
  const fmt = useRestaurantFormatters();
  const isRTL = i18n.language.toLowerCase().startsWith("ar");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerElsRef = useRef<HTMLButtonElement[]>([]);
  const [activeBranch, setActiveBranch] = useState<ActiveBranch | null>(null);

  const branchMetricById = useMemo(() => {
    const map = new Map(metrics.branches.map(b => [b.id, b]));
    return map;
  }, [metrics.branches]);

  const maxRevenue = useMemo(
    () => metrics.branches.reduce((max, b) => Math.max(max, b.revenue), 1),
    [metrics.branches],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {return;}

    const map = new maplibregl.Map({
      container: el,
      style: "https://demotiles.maplibre.org/style.json",
      center: [44.5, 24.2],
      zoom: 4.4,
      attributionControl: false,
    });
    map.addControl(
      new maplibregl.NavigationControl({ showCompass: false }),
      isRTL ? "top-left" : "top-right",
    );
    mapRef.current = map;

    return () => {
      markerElsRef.current.forEach(node => node.remove());
      markerElsRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRTL]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {return;}

    markerElsRef.current.forEach(node => node.remove());
    markerElsRef.current = [];

    const visibleBranches = BRANCHES.filter(branch => branchMetricById.has(branch.id));

    visibleBranches.forEach((branch, index) => {
      const metric = branchMetricById.get(branch.id);
      if (!metric) {return;}

      const size = 14 + (metric.revenue / maxRevenue) * 26;
      const button = document.createElement("button");
      button.type = "button";
      button.className = "restaurant-branch-map__pin";
      button.style.width = `${size}px`;
      button.style.height = `${size}px`;
      button.style.backgroundColor = CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length];
      button.setAttribute("aria-label", isRTL ? branch.nameAr : branch.name);

      const showDetails = () => {
        const point = map.project([branch.lng, branch.lat]);
        setActiveBranch({
          id: branch.id,
          name: isRTL ? branch.nameAr : branch.name,
          city: isRTL ? branch.cityAr : branch.city,
          revenue: metric.revenue,
          orders: metric.orders,
          avgPrepMinutes: branch.avgPrepMinutes,
          x: point.x,
          y: point.y,
        });
      };
      const hideDetails = () => setActiveBranch(null);

      button.addEventListener("mouseenter", showDetails);
      button.addEventListener("mouseleave", hideDetails);
      button.addEventListener("focus", showDetails);
      button.addEventListener("blur", hideDetails);
      button.addEventListener("click", showDetails);

      new maplibregl.Marker({ element: button, anchor: "center" })
        .setLngLat([branch.lng, branch.lat])
        .addTo(map);

      markerElsRef.current.push(button);
    });

    setActiveBranch(null);
  }, [branchMetricById, maxRevenue, isRTL]);

  return (
    <RestaurantSectionCard
      title={t("restaurantAnalytics.charts.branchMap", { defaultValue: "Branch map" })}
      tip={t("restaurantAnalytics.charts.branchMapTip", {
        defaultValue: "Bubble size reflects revenue for the selected period.",
      })}
    >
      <div className="restaurant-branch-map">
        <div ref={containerRef} className="restaurant-branch-map__canvas" role="presentation" />
        {activeBranch ? (
          <div
            className="restaurant-branch-map__popup"
            style={{ left: activeBranch.x, top: activeBranch.y }}
            role="tooltip"
          >
            <p className="restaurant-branch-map__popup-name">{activeBranch.name}</p>
            <p className="restaurant-branch-map__popup-city">{activeBranch.city}</p>
            <p>
              {t("restaurantAnalytics.charts.revenue", { defaultValue: "Revenue" })}:{" "}
              <strong>{fmt.formatCurrency(activeBranch.revenue, { compact: true })}</strong>
            </p>
            <p>
              {t("restaurantAnalytics.charts.orders", { defaultValue: "Orders" })}:{" "}
              <strong>{fmt.formatNumber(activeBranch.orders)}</strong>
            </p>
            <p>
              {t("restaurantAnalytics.branches.avgPrepTime", { defaultValue: "Avg. prep time" })}:{" "}
              <strong>{fmt.formatMinutes(activeBranch.avgPrepMinutes, 0)}</strong>
            </p>
          </div>
        ) : null}
      </div>
    </RestaurantSectionCard>
  );
};

export default RestaurantBranchMap;
